import { controlCenter } from "@ventionco/mm-machine-logic-robot";
import * as machinemotion from "../api/machinemotion";
import helpers from "../helpers";
import widgets from "../legacy/widgets";
import environment from "../environment";
import { Config } from "./Config";
import { Notification } from "./Notification";
import { doAjaxPromise, getRemoteHost } from "../api/api_request";
import { action, computed, makeObservable, observable } from "mobx";
import { FeatureFlags } from "../featureFlags";
const { controller: { features: controlCenterFeaturesFactory, initialize: initializeRobotAppsService }, utils: { executionEngineConfig_updateWithRobotAppsMachineContext } } = controlCenter;

/**
 * Manages a list of configurations, each associated with a different controller.
 * Also provides a useful set of aggregation methods to access data models as if
 * they were a flat list.
 *
 * @param {Library} pLibrary
 * @param {*} pConfigInfo
 * @extends {Notification} Notification
 */
export function ConfigManager(pLibrary, pConfigInfo) {
  Notification.call(this);

  this.mHostControllerUuid      = pConfigInfo.hostControllerUuid;
  this.mLibrary                 = pLibrary;
  this.mConfigurationList       = [];
  this.mDirty                   = false;
  this.mUUID                    = undefined;
  this.mVersion                 = this.mLibrary.getLibraryVersion();
  this.mParentControllerId      = undefined;
  this.mMaxNumberOfControllers  = 8;
  this.mDetectedControllers     = [];
  this.isChanged                = false;  // used in the CAD
  this.mLastValidConfigPayload  = undefined;

  makeObservable(this, {
    mConfigurationList: observable,
    mDirty: observable,
    inputs: computed,
    dirty: computed,
    fromJson: action,
    addConfiguration: action,
    newConfiguration: action,
    removeConfiguration: action,
    reset: action,
    setDirty: action,
    _debouncedSetDirty: action,
    deploy: action,
  });
}

ConfigManager.prototype = Object.create(Notification.prototype);
ConfigManager.prototype.constructor = ConfigManager;

ConfigManager.prototype.getName = function() { return 'Configuration Manager'; };

ConfigManager.prototype.getAndUpdateConfiguredNetworkStatus = function(pOnComplete) {
  const lThis = this;
  machinemotion.getConfiguredNetworkStatus((pNetworkStatus) => {
    lThis.mHostControllerUuid = pNetworkStatus.hostController;
    Object.keys(pNetworkStatus.configuredControllers).forEach((pControllerUuid) => {
      const lConfig = lThis.getConfiguration(pControllerUuid);
      if (lConfig) {
        lConfig.getController().mIsControllerAvailable = pNetworkStatus.configuredControllers[pControllerUuid].reachable;
      }
    });
    // NOTE (AJ): With no auto sync, and the option for the user to set the ip address manually, we introduced
    // the chance of the user assigning two controllers with ip addresses that belong to the same controller (LAN1 & LAN2).
    // This is a problem since the activateConfiguration function call in the exec-engine will fail and kill the exec-engine. ( this is under investigation )
    //
    // In the time being to prevent this, we need to validate if the ip address entered by the used does not belong to the same controller and the information
    // about all the ipaddresses that belong to a controller is fetched by a GET request to /network/detectedNetwork.
    //
    // Without auto sync this request was never made, so we need to make it here so that we cache the latest detected network state which will be used by
    // Controller.validateIpAddress.
    machinemotion.getDetectedNetwork(function(pDetectedNetwork) {
      lThis.mDetectedControllers = pDetectedNetwork.detectedControllers;
      if (pOnComplete) { pOnComplete(pNetworkStatus); }
    });
  });
};

ConfigManager.prototype.autoPopulateConfigurations = function(pCallback) {
  const lThis = this;
  machinemotion.getDetectedNetwork(function(pDetectedNetwork) {
    pDetectedNetwork.detectedControllers.forEach((pController) => {
      let lConfig = lThis.getConfiguration(pController.configControllerUuid);
      if ( lConfig === undefined ) {
        lConfig = lThis.newConfiguration();
        const lController = lConfig.getController();
        const lFormattedHardwareVersion = lController.formatHardwareVersion(pController.hardwareVersion);
        lController.setHardwareVersion(lFormattedHardwareVersion);
        lController.setIpAddress(pController.ipAddresses[0]);
        lController.mIsControllerAvailable = true;

        // NOTE: this assumes that the first configured is the parent, this is most likely true if you are connected to the parent.
        // Currently, the order is determined by mm-discovery in this way, first device is the host (device itself) and the other are
        // whatever order they were received in.
        if (lThis.getParentControllerId() === undefined) {
          lThis.setParentController(lController.getUUID());
        }
      }
    });
    pCallback && pCallback();
  });
};

/**
 * Populates the configuration manager from data retrieved from the machinemotion.js
 * end point.
 *
 * @param {Function<Boolean>} pCompletion Callback when completed. First argument is success.
 * @param {import('../MachineLogicServices').MachineLogicServices} machineLogicServices
 */
ConfigManager.prototype.loadFromLibrary = function(pCompletion, machineLogicServices) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lThis = this;
  const lOnComplete = function(pSuccess, pError) {
    if (pSuccess) {
      pCompletion(true);
    } else {
      console.error('Failed to initialize the library. Error: ' + pError);
      pCompletion(false);
    }
  };

  machinemotion.library(function(pData) {
    if(!pData) {
      lOnComplete(false, 'Could not fetch the library.');
      return;
    }

    if(pData.includes('#global')) {
      machinemotion.application('#global', function(pAppData) {
        const props = {
          doAjaxPromise,
          helpers: {
            isProdDeployment: helpers.isProdDeployment,
            getExpressionValidationFunction: helpers.getExpressionValidationFunction,
            getMinMaxValidationFunction: helpers.getMinMaxValidationFunction,
          },
          library: lThis.mLibrary,
          getRemoteHost,
          widgets: {
            clearMessageBox: widgets.clearMessageBox,
            messageBox: widgets.messageBox,
          },
          robotTypes: lThis.mLibrary.mInstructionSet.config.robot.parameters.find(paramObj => /robotType/i.test(paramObj.name)).values,
          onRobotConfigChanged: () => lThis.mLibrary.mConfigManager.setDirty(),
          onRobotConfigAdded: (config) => config.notify('onRobotAdded', lThis.mLibrary.robotAppsService.selectors.getRobotConfig()),
          onRobotConfigRemoved: config => config.notify('onRobotRemoved'),
          onRobotNameChanged: (config, name) => config.notify('onRobotNameChanged', name),
          onVerifyConfigurationClick: () => {
            machineLogicServices.NavigationService.navigateToMachineConfig();
          },
          RobotClipsOnRobotClipChange: robotClip => {
            lThis.mLibrary.mCurrentApplication?.notify('onInstructionChanged', robotClip);
          },
          ReferenceFramesOnTeachWaypointAndCloseButtonClick: (previousReferenceFrame) => {
            lThis.mLibrary.mCurrentApplication.mSequences.forEach(sequence => {
              sequence.mInstructions.forEach(instruction => {
                const instructionCalibrationReferenceFrame = instruction.getParameter('calibrationReferenceFrame');
                if (instructionCalibrationReferenceFrame && previousReferenceFrame && instructionCalibrationReferenceFrame.vid === previousReferenceFrame.vid) {
                  instruction.mParameters["calibrationReferenceFrame"] = {
                    ...instructionCalibrationReferenceFrame,
                    arrayMatrix__referenceFrame_robotKinematicsChainRoot: previousReferenceFrame.arrayMatrix__referenceFrame_robotKinematicsChainRoot,
                  };
                }
              });
            });
            lThis.mLibrary.mCurrentApplication.setDirty();
          },
          ReferenceFramesOnDeleteButtonClick: () => lThis.mLibrary.mCurrentApplication.setDirty(),
        };

        const validator = lThis.mLibrary.getValidator();
        lThis.mLibrary.robotAppsService = initializeRobotAppsService(controlCenterFeaturesFactory.create(props), validator, FeatureFlags);

        lThis.setLastValidConfigPayload(pAppData);
        lThis.fromJson(pAppData);
        // ignore the dirty flag set while loading the config, needed for the apply configuration button, to not be active when built
        lThis.mDirty = false;

        lThis.initializeConfigChecker(() => {
          // NOTE: Doing an activation to have the MM being configured exactly as the configuration, in case it was change by some other scripts (python)
          machinemotion.activateConfiguration(function(pData) {
            if(pData) {
              lThis.notify('onInitialized');
              lOnComplete(true);
            }
          });
        });

      });
    } else {
      if (!lThis.mLibrary.robotAppsService) {
        throw new Error("No global app detected and the robotAppsService has not been initialized. This should not happen and might be due to an invalid execution engine state.");
      }

      lThis.setDirty();
      lThis.deploy(function(pData) {
        if(pData) {
          lThis.notify('onInitialized');
          lOnComplete(true);
        } else {
          lOnComplete(false, 'Failed to deploy.');
        }
      });
    }
  });
};

ConfigManager.prototype.initializeConfigChecker = function(pCompletion) {
  let lConfigCheckerCompatibilityListInitializationsRemaining = this.mConfigurationList.length;
  // Initialize Config.mCompatibleAxisTypesForConfigChecker for each config.
  this.mConfigurationList.forEach((pConfig) => {
    pConfig.initializeCompatibleAxisTypesForConfigChecker(() => {
      lConfigCheckerCompatibilityListInitializationsRemaining--;
      if (lConfigCheckerCompatibilityListInitializationsRemaining === 0) {
        pCompletion();
      }
    });
  });
};

/**
 * Inflates the ConfigManager from the provided data. This data is actually an Application
 * in the same shape as is returned from 'toJson' here.
 *
 * @param {Object} pData
 */
ConfigManager.prototype.fromJson = function(pData) {
  const lThis = this;
  const lSequences = pData.sequences;

  this.mUUID                = pData.uuid;
  this.mVersion             = pData.version;

  if (!lSequences || lSequences.length == 0) {
    console.warn('Data did not contain any startup sequence.', pData);
    return;
  }

  const lInstructions = lSequences[0].instructions;
  if (!lInstructions) {
    console.warn('Data did not contain any startup instructions.', pData);
    return;
  }

  // Set the controller ID
  this.mParentControllerId = lSequences[0].parameters.parentController;

  // We group instruction data based off of controller IDs. Each controller ID key in this
  // map points to a list of instructions.
  const lControllerIdToInstList = {};

  lInstructions.sort(function(pFirst, pSecond) {
    // Sort the  controller instructions to the front of the list.
    if(pFirst.name === 'controller' && pSecond.name === 'controller') { return 0; } // if both first and second are controllers, keep the same order
    if(pFirst.name === 'controller' && pSecond.name !== 'controller') { return -1; }
    if(pFirst.name !== 'controller' && pSecond.name === 'controller') { return 1; }
    return 0; // if none are controller, keep the same order.
  });

  lInstructions.forEach(function(pInstruction) {
    if (pInstruction.category !== 'config') {
      console.error('Unknown instruction', pInstruction);
      return;
    }

    const lControllerId = pInstruction.name === 'controller' ? pInstruction.parameters.uuid : pInstruction.parameters.controllerId;

    if (!lControllerId) {
      console.error('This instruction does NOT reference a controller. This should not be happening.', pInstruction);
      return;
    }

    if (!lControllerIdToInstList[lControllerId]) {
      if (pInstruction.name !== 'controller') {
        console.warn('This instruction references a controller that does not exist. This should not be happening.', pInstruction);
      }
      lControllerIdToInstList[pInstruction.parameters.uuid] = [];
    }

    lControllerIdToInstList[lControllerId].push(pInstruction);
  });

  this.mConfigurationList = Object.keys(lControllerIdToInstList).map(function(pControllerId) {
    const lInstructionList = lControllerIdToInstList[pControllerId];
    return new Config(lThis, lThis.mLibrary, lInstructionList);
  });
};

ConfigManager.prototype.toJson = function() {
  let lInstructionList = [];

  this.mConfigurationList.forEach(function(pConfiguration) {
    lInstructionList = lInstructionList.concat(pConfiguration.toJson());
  });

  return {
    'name': '#global',
    'initialSequence': '#startup',
    'version': this.mVersion,
    'uuid': this.mUUID,
    'sequences': [
      {
        'name': '#startup',
        'parameters': {
          'parentController': this.mParentControllerId,
        },
        'instructions': lInstructionList
      }
    ]
  };
};

/**
 * Returns the Configuration list.
 *
 * @returns {Config[]} List of available Controllers.
 */
ConfigManager.prototype.getConfigurationList = function() {
  return this.mConfigurationList;
};

/**
 * Adds a controller with the provided data and creates a Controller object from it.
 *
 * @param {Object | undefined} pData
 * @returns {Config} The controller that has been added. Null if the add was unsuccessful.
 */
ConfigManager.prototype.addConfiguration = function(pData) {
  const lConfig = new Config(this, this.mLibrary, pData);
  this.mConfigurationList.push(lConfig);
  this.notify('onConfigurationAdded', lConfig, pData === undefined);
  this.setDirty();
  return lConfig;
};

/**
 * Creates and adds a new Configuration.
 *
 * @returns {Config} The newly created controller object.
 */
ConfigManager.prototype.newConfiguration = function() {
  return this.addConfiguration();
};

/**
 * Checks if a Configuration exists with the provided Controller ID.
 *
 * @param {String} pUuid Unique ID of a Controller.
 * @returns {Boolean} True if the Configuration exists, otherwise false.
 */
ConfigManager.prototype.hasConfiguration = function(pUuid) {
  return this.getConfiguration(pUuid) != undefined;
};

/**
 * Finds the Configuration with the provided Controller ID.
 *
 * @param {String} pUuid Unique ID of a Controller.
 * @returns {Config} Configuration associated with the UUID, otherwise null.
 */
ConfigManager.prototype.getConfiguration = function(pUuid) {
  return this.mConfigurationList.find(function(pController) { return pController.getController().getUUID() === pUuid; });
};

/**
 * Finds the configuration whose controller has been designated as the parent. If none exists,
 * the first controller in the list is returned as the parent controller.
 *
 * If there are no configurations in the list, undefined is returned.
 *
 * @returns {Config | undefined} The parent configuration, or undefined if there are no configurations.
 */
ConfigManager.prototype.getParentConfiguration = function() {
  if (this.mConfigurationList.length === 0) {
    console.error('Unable to resolve the parent configuration.');
    return undefined;
  }

  const lParentConfig = this.getConfiguration(this.getParentControllerId());
  return lParentConfig || this.mConfigurationList[0];
};

/**
 * Finds and removes a Configuration from the Library.
 *
 * @param {String} pUuid Unique ID of the Controller to be removed.
 * @returns {Boolean} True if the removal was successful, otherwise false.
 */
ConfigManager.prototype.removeConfiguration = function(pUuid) {
  const lConfigIndex = this.mConfigurationList.findIndex(function(pConfig) { return pConfig.getController().getUUID() === pUuid; });
  if (lConfigIndex < 0) {
    return false;
  }

  const lRemovedConfig = this.mConfigurationList.splice(lConfigIndex, 1)[0];

  this.notify('onConfigurationRemoved', lRemovedConfig, lConfigIndex);
  this.setDirty();
  return true;
};

/**
 * Sets the parent controller to the provided ID.
 * @param {String} pUuid ID of a valid controller.
 */
ConfigManager.prototype.setParentController = function(pUuid) {
  this.mParentControllerId = pUuid;
  this.notify('onParentConfigurationChanged', this.mParentControllerId);
  this.setDirty();
};

/**
 * Returns the parent controller unique ID.
 * @returns {String} Unique ID of a controller.
 */
ConfigManager.prototype.getParentControllerId = function() { return this.mParentControllerId; };

ConfigManager.prototype._debouncedErrorValidation = helpers.debounce(function() {
  this.mLibrary.getValidator().runValidation();
}, 100);

ConfigManager.prototype._debouncedSetDirty = helpers.debounce(function() {
  this.mLibrary.getValidator().runValidation();
  // Set timeout to deploy app after 500ms
  if (!this.mDirty) {  // No need to stack multiple deploys
    this.mDirty = true;
    // Deploy app if needed
    this.deploy();
  }
}, 500);

ConfigManager.prototype.setDirty = function() {
  if(environment.isOnPendant() && !environment.isOnConfigCheckerPage()) {
    // On the Pendant the configuration is applied manually, with the [Apply configuration] button.
    // This "Apply" button listens to the 'onSetDirty' notification (it starts listening after the initialization).
    // Clicking the button calls the Config.deploy
    this.mDirty = true;
    this.notify('onSetDirty');
    this._debouncedErrorValidation();
  }
  else {
    this.isChanged = true;
    this._debouncedSetDirty();
  }
};

ConfigManager.prototype.deploy = function(pCompletion) {
  const lThis = this;

  if(this.mDirty) {
    lThis.notify('onConfigUpdate');
    const selectors = lThis.mLibrary.getRobotAppsService().selectors;
    machinemotion.update(executionEngineConfig_updateWithRobotAppsMachineContext(this.toJson(), environment.isOnPendant() ? selectors.getRobotConfig() : null), function(pData) {
      if(pData) {
        lThis.mDirty = false;
        lThis.mUUID = pData.uuid;
        lThis.getAndUpdateConfiguredNetworkStatus(function() {
          lThis.setLastValidConfigPayload(pData);
          if (pCompletion) pCompletion(pData);
          lThis.listAxes()
            .filter(pAxis => pAxis.isConfigCheckerCompatible())
            .forEach(pAxis => pAxis.updateConfigCheckerResultStatus());
          lThis.notify('onConfigDeployed');
        });
      }
      else if (pData == null){
        // Update failed. Allow further updates.
        lThis.mDirty = false;
        if (pCompletion) pCompletion(null);
      }

      lThis.isChanged = false;
    });
  } else {
    if (pCompletion) pCompletion(true);
  }
};

ConfigManager.prototype._aggregateList = function(pGetItems) {
  let lAggregatedList = [];
  this.mConfigurationList.forEach(function(pConfig) {
    lAggregatedList = lAggregatedList.concat(pGetItems(pConfig));
  });
  return lAggregatedList;
};

/**
 * used to check all of the inputs including pneumatic devices that might use these inputs.
 * Useful for setting variable from input.
 * @returns
 */
ConfigManager.prototype.numberOfInputs = function() {
  const lNumberOfUsedInputsArray = this._aggregateList((pConfig) => { return pConfig.numberOfInputs(); });
  const lSumOfUsedInputs = lNumberOfUsedInputsArray.reduce((previous, current) => previous + current, 0);
  return lSumOfUsedInputs;
};

ConfigManager.prototype.listInputs = function() {
  return this._aggregateList(function(pConfig) { return pConfig.listInputs(); });
};

ConfigManager.prototype.listOutputs = function() {
  return this._aggregateList(function(pConfig) { return pConfig.listOutputs(); });
};

ConfigManager.prototype.listAxes = function() {
  return this._aggregateList(function(pConfig) { return pConfig.listAxes(); });
};

ConfigManager.prototype.listRobots = function() {
  return this._aggregateList(function(pConfig) { return pConfig.listRobots(); });
};

ConfigManager.prototype.getAxisByUuid = function(pUuid) {
  return this.listAxes().find(function(pAxis) { return pAxis.mUUID === pUuid; });
};

ConfigManager.prototype.numberOfAxes = function() {
  let lNumAxes = 0;
  this.mConfigurationList.forEach(function(pConfig) {
    lNumAxes += pConfig.numberOfAxes();
  });
  return lNumAxes;
};

ConfigManager.prototype.numberOfRealAxes = function() {
  let lNumRealAxes = 0;
  this.mConfigurationList.forEach(function(pConfig) {
    lNumRealAxes += pConfig.numberOfRealAxes();
  });
  return lNumRealAxes;
};

ConfigManager.prototype.numberOfNonMotorizedActuators = function() {
  return this.listAxes().filter(function(pAxis) { return pAxis.isNonMotorized(); }).length;
};

ConfigManager.prototype.numberOfRobots = function() {
  return this.listRobots().length;
};

ConfigManager.prototype.getInputByUuid = function(pUuid) {
  return this.listInputs().find(function(pInput) { return pInput.getUUID() === pUuid; });
};

ConfigManager.prototype.getInputByName = function(pName) {
  return this.listInputs().find(function(pInput) { return pInput.getName() === pName; });
};

ConfigManager.prototype.getOutputByUuid = function(pUuid) {
  return this.listOutputs().find(function(pOutput) { return pOutput.getUUID() === pUuid; });
};

ConfigManager.prototype._getConfigDependant_DegAndMMPresence = function () {
  // The parameters that have the type configDependant depend on the type of axis currently in the config
  let _lMmPresence = false;
  let _lDegPresence = false;

  this.listAxes().forEach(function(pAxis) {
    if (pAxis.getUnits() == "deg"){
      _lDegPresence = true;
    }
    else if (pAxis.getType() != "pneumatic"){
      _lMmPresence = true;
    }
  });

  return {_lMmPresence : _lMmPresence, _lDegPresence : _lDegPresence };
};

ConfigManager.prototype._getConfigDependant_Unit = function(){
  // Determine the units of the parameter of type configDependant
  const _lMmPresence  = this._getConfigDependant_DegAndMMPresence()._lMmPresence;
  const _lDegPresence = this._getConfigDependant_DegAndMMPresence()._lDegPresence;

  let _lUnit = "mm"; // Default value
  if (_lDegPresence && !_lMmPresence){
    _lUnit = "deg";
  }
  return _lUnit;
};

ConfigManager.prototype.reset = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  this.mConfigurationList.forEach(function(pConfig) {
    pConfig.reset();
  });
  this.mConfigurationList     = [];
  this.mParentControllerId    = undefined;
  this.mDirty                 = false;
  this.isChanged              = false;
  this.notify('onReset');
};

/**
 * Retrieves the hardware versions that we support. Please note that these
 * are not at all legit hardware versions. This is intentional. The hardware
 * version simply needs to make our regex happy: the actual minor versions
 * do not matter to us at all. Hence, we provide just a V2 and V2 1-Drive
 * hardware version. V1 is dead to us.
 * @returns
 */
ConfigManager.prototype.listAvailableHardwareVersions = function() {
  const lHardwareOptions = this.mLibrary.getInstructionSet().config.controller.parameters.find(param => param.name === 'hardwareVersion').values;

  return lHardwareOptions;
};

/**
 * Returns the unique id of the controller that is currently running the
 * execution engine that we are talking to. This is useful for disabling
 * UI elements for child controllers.
 * @returns {String} ControllerID
 */
ConfigManager.prototype.getHostControllerUuid = function() {
  return this.mHostControllerUuid;
};

/**
 * TODO: Temporary bandaid to manually set the host controller uuid on an initial sync.
 * @params {String} ControllerID
 */
ConfigManager.prototype.setHostControllerUuid = function(pUuid) {
  this.mHostControllerUuid = pUuid;
};

/**
 * Returns whether or not the execution engine that we are talking to is
 * running on the parent controller. This is useful for disabling
 * UI elements for child controllers.
 *
 * @returns {Boolean} true if the execution engine is on the parent controller, otherwise false.
 */
ConfigManager.prototype.hasParentAccessLevel = function() {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  return !this.getHostControllerUuid() || this.getHostControllerUuid() === this.getParentControllerId();
};

/**
 *
 * @param {import('./ApplicationBase').ApplicationBase} pApp
 */
ConfigManager.prototype.addApplicationInCalibrationParts = function(pApp) {
  for (const config of this.mConfigurationList) {
    config.addApplicationInCalibrationParts(pApp);
    if ( this.mDirty ) {
      this.deploy(
        function(result) {
          if(result === null) {
            console.error('failed to deploy configuration');
            return;
          }
        }
      );
    }
  }
};

ConfigManager.prototype.removeApplicationInCalibrationParts = function(pApp) {
  for(const config of this.mConfigurationList) {
    config.removeApplicationInCalibrationParts(pApp);
  }
};

/**
 * Sets the last valid config payload. This is used to revert changes to the last save.
 * @param {Object} pValidConfigPayload
 */
ConfigManager.prototype.setLastValidConfigPayload = function(pValidConfigPayload) {
  this.mLastValidConfigPayload = pValidConfigPayload;
};

/**
 * Reverts changes to the last save.
 *
 * Note: When this function is called, all the ui hooks will be broken as fromJson of the config manager remove all existing
 * config objects and create new ones.
 */
ConfigManager.prototype.revertChangesToLastSave = function() {
  this.fromJson(this.mLastValidConfigPayload);
};

Object.defineProperty(
  ConfigManager.prototype,
  'inputs',
  {
    get: function () {
      return this.listInputs();
    }
  }
);


Object.defineProperty(
  ConfigManager.prototype,
  'dirty',
  {
    get: function () {
      return this.mDirty;
    },
    set: function (pDirty) {
      this.mDirty = pDirty;
    }
  }
);


